// useNodeViewsState.js

import { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { getPluralLabel } from 'utilities/Languages';
import { prefersAlphaListDefaultView } from 'utilities/NodeType';
import {
  CATCH_ALL_VIEW_ID,
  getViewRowIdentity,
  isDashboardCreatedView,
  isCatchAllView,
} from 'utilities/ViewRows';

// Services
import { subscribeToViews } from 'services/views/ViewReadService';

const DEFAULT_VIEW_ID = CATCH_ALL_VIEW_ID;
const NODE_VIEW_MEMORY_STORAGE_KEY = 'nodal:lastNodeViewByScope:v1';
const NODE_VIEW_DEBUG_FLAG = '__NODE_VIEW_DEBUG__';
const REMEMBERED_VIEW_GRACE_MS = 700;

const resolveViewIdentity = (view) => {
  const identity = getViewRowIdentity(view);
  if (identity) return identity;
  const localDefaultId = String(view?.id || '').trim();
  return localDefaultId === DEFAULT_VIEW_ID ? DEFAULT_VIEW_ID : '';
};

const resolveCatchAllView = (views) => {
  const list = Array.isArray(views) ? views : [];
  return list.find((view) => isCatchAllView(view) && view?.isDefault === true)
    || list.find((view) => isCatchAllView(view))
    || null;
};

const resolveCatchAllViewId = (views) => {
  return resolveViewIdentity(resolveCatchAllView(views)) || DEFAULT_VIEW_ID;
};

const getViewByIdentity = (views, identity) => {
  const list = Array.isArray(views) ? views : [];
  if (!identity) return null;
  return list.find((view) => resolveViewIdentity(view) === identity) || null;
};

const canRememberView = (views, identity) => {
  const view = getViewByIdentity(views, identity);
  if (!view) return false;
  return !isCatchAllView(view);
};

const normalizeScopePart = (value) => String(value || '').trim().toLowerCase();

const getScopeMemoryKey = ({ baseKey, parentTypeKey, relatedTypeKey }) => {
  const base = normalizeScopePart(baseKey) || '__base__';
  const parent = normalizeScopePart(parentTypeKey) || '__parent__';
  const related = normalizeScopePart(relatedTypeKey) || '__related__';
  return `${base}::${parent}::${related}`;
};

const readStoredViewMap = () => {
  if (typeof window === 'undefined' || !window?.localStorage) return {};

  try {
    const raw = window.localStorage.getItem(NODE_VIEW_MEMORY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStoredViewMap = (nextMap) => {
  if (typeof window === 'undefined' || !window?.localStorage) return;

  try {
    window.localStorage.setItem(NODE_VIEW_MEMORY_STORAGE_KEY, JSON.stringify(nextMap || {}));
  } catch {
    // Storage write failures should not block view rendering.
  }
};

const getStoredViewIdForScope = (scopeMemoryKey) => {
  if (!scopeMemoryKey) return '';
  const map = readStoredViewMap();
  return String(map?.[scopeMemoryKey] || '').trim();
};

const setStoredViewIdForScope = (scopeMemoryKey, viewId) => {
  if (!scopeMemoryKey) return;
  const normalizedViewId = String(viewId || '').trim();
  if (!normalizedViewId) return;

  const map = readStoredViewMap();
  if (map?.[scopeMemoryKey] === normalizedViewId) return;

  writeStoredViewMap({
    ...map,
    [scopeMemoryKey]: normalizedViewId,
  });
};

const clearStoredViewIdForScope = (scopeMemoryKey) => {
  if (!scopeMemoryKey) return;
  const map = readStoredViewMap();
  if (!Object.prototype.hasOwnProperty.call(map, scopeMemoryKey)) return;

  const nextMap = { ...map };
  delete nextMap[scopeMemoryKey];
  writeStoredViewMap(nextMap);
};

const isDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  return Boolean(window?.[NODE_VIEW_DEBUG_FLAG]);
};

const debugLog = (...args) => {
  if (!isDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log('[node-view-memory]', ...args);
};

const ensureCatchAllView = (views, defaultView) => {
  const list = Array.isArray(views) ? views : [];
  if (!list.length) return [defaultView];
  if (list.some((view) => isCatchAllView(view))) return list;
  return [defaultView, ...list];
};

export default function useNodeViewsState({ node, activeTypeKey, baseKey }) {
  const language = useCurrentLanguage();
  const { getType } = useNodeTypes();
  const scopedTypeKey = activeTypeKey || null;
  const parentTypeKey = node?.type || null;
  const scopeKey = `${baseKey || ''}::${parentTypeKey || ''}::${scopedTypeKey || ''}`;
  const scopeMemoryKey = useMemo(() => getScopeMemoryKey({
    baseKey,
    parentTypeKey,
    relatedTypeKey: scopedTypeKey,
  }), [baseKey, parentTypeKey, scopedTypeKey]);

  const defaultViewLabel = useMemo(() => {
    const typeKey = scopedTypeKey || node?.type || 'NODE';
    const def = getType(typeKey);
    return `All ${getPluralLabel(def, typeKey, language)}`;
  }, [scopedTypeKey, node?.type, getType, language]);

  const defaultViewType = useMemo(() => {
    const typeKey = scopedTypeKey || node?.type || 'NODE';
    const def = getType(typeKey);
    const candidate = typeof def?.defaultView === 'string' ? def.defaultView.trim().toUpperCase() : '';

    const allowed = new Set(['TABLE', 'LIST', 'ALPHA_LIST', 'BOARD', 'GALLERY', 'FEED', 'CALENDAR', 'TIMELINE']);
    if (allowed.has(candidate)) {
      if (candidate === 'LIST' && prefersAlphaListDefaultView(typeKey)) {
        return 'ALPHA_LIST';
      }
      return candidate;
    }
    return prefersAlphaListDefaultView(typeKey) ? 'ALPHA_LIST' : 'LIST';
  }, [scopedTypeKey, node?.type, getType]);

  // Firestore-backed view list + the currently active view id.
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewIdState] = useState(DEFAULT_VIEW_ID);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedScopeKey, setLoadedScopeKey] = useState('');
  const rememberedWaitRef = useRef({
    scopeKey: '',
    timer: null,
    pending: false,
    lastRows: [],
  });

  const defaultView = useMemo(() => (
    { key: DEFAULT_VIEW_ID, label: defaultViewLabel, type: defaultViewType }
  ), [defaultViewLabel, defaultViewType]);

  const isScopePending = useMemo(() => {
    if (!baseKey || !scopedTypeKey) return false;
    return loadedScopeKey !== scopeKey;
  }, [baseKey, scopedTypeKey, loadedScopeKey, scopeKey]);

  // Load base views so node details can reuse shared type-scoped views.
  useEffect(() => {
    const clearRememberedWait = () => {
      const current = rememberedWaitRef.current;
      if (current.timer) {
        clearTimeout(current.timer);
      }
      rememberedWaitRef.current = {
        scopeKey: '',
        timer: null,
        pending: false,
        lastRows: [],
      };
    };

    if (!baseKey) {
      clearRememberedWait();
      setViews([]);
      setActiveViewIdState(DEFAULT_VIEW_ID);
      setIsLoading(false);
      setLoadedScopeKey('');
      return undefined;
    }

    if (!scopedTypeKey) {
      clearRememberedWait();
      setViews([]);
      setActiveViewIdState(DEFAULT_VIEW_ID);
      setIsLoading(false);
      setLoadedScopeKey('');
      return undefined;
    }

    // Mark scope as loading immediately so consumers can avoid rendering stale view state.
    clearRememberedWait();
    setLoadedScopeKey('');
    setIsLoading(true);

    let cancelled = false;

    const resolveFromRows = (rows) => {
      const nextRows = (Array.isArray(rows) ? rows : [])
        .filter((view) => !isDashboardCreatedView(view));
      const baseViews = ensureCatchAllView(nextRows, defaultView);
      const filtered = baseViews.filter((view) => {
        const viewType = view?.nodeType || view?.typeKey || null;
        return !scopedTypeKey || !viewType || viewType === scopedTypeKey;
      });
      const nextResolvedViews = ensureCatchAllView(filtered, defaultView);
      const identities = new Set(nextResolvedViews.map((view) => resolveViewIdentity(view)).filter(Boolean));
      const rememberedId = getStoredViewIdForScope(scopeMemoryKey);
      const catchAllId = resolveCatchAllViewId(nextResolvedViews);
      const firstId = resolveViewIdentity(nextResolvedViews[0]);
      const rememberedExists = rememberedId ? identities.has(rememberedId) : false;

      return {
        nextRows,
        nextResolvedViews,
        identities,
        rememberedId,
        catchAllId,
        firstId,
        rememberedExists,
      };
    };

    const finalizeSelection = (rows, { allowRemembered = true, reason = 'hydrate' } = {}) => {
      if (cancelled) return;

      const resolved = resolveFromRows(rows);
      const {
        nextRows,
        nextResolvedViews,
        identities,
        rememberedId,
        catchAllId,
        firstId,
        rememberedExists,
      } = resolved;

      const candidateIds = allowRemembered
        ? [rememberedId, catchAllId, firstId]
        : [catchAllId, firstId];
      const nextActiveViewId = candidateIds.find((id) => id && identities.has(id)) || catchAllId;

      setViews(nextRows);
      setActiveViewIdState(nextActiveViewId || DEFAULT_VIEW_ID);
      if (canRememberView(nextResolvedViews, nextActiveViewId)) {
        setStoredViewIdForScope(scopeMemoryKey, nextActiveViewId || DEFAULT_VIEW_ID);
      } else if (!allowRemembered && rememberedId && !rememberedExists) {
        clearStoredViewIdForScope(scopeMemoryKey);
      }

      debugLog('hydrate', {
        reason,
        scopeKey,
        scopeMemoryKey,
        rememberedId,
        rememberedExists,
        catchAllId,
        firstId,
        nextActiveViewId,
        resolvedViewIds: nextResolvedViews.map((view) => resolveViewIdentity(view)).filter(Boolean),
      });

      setLoadedScopeKey(scopeKey);
      setIsLoading(false);
    };

    const unsubscribe = subscribeToViews(
      { baseKey, nodeType: scopedTypeKey },
      (rows) => {
        if (cancelled) return;

        const resolved = resolveFromRows(rows);
        const { rememberedId, rememberedExists } = resolved;

        if (rememberedId && !rememberedExists) {
          rememberedWaitRef.current.lastRows = resolved.nextRows;

          if (!rememberedWaitRef.current.pending || rememberedWaitRef.current.scopeKey !== scopeKey) {
            rememberedWaitRef.current.scopeKey = scopeKey;
            rememberedWaitRef.current.pending = true;
            rememberedWaitRef.current.timer = setTimeout(() => {
              rememberedWaitRef.current.pending = false;
              rememberedWaitRef.current.timer = null;
              finalizeSelection(rememberedWaitRef.current.lastRows, {
                allowRemembered: false,
                reason: 'remembered-timeout',
              });
            }, REMEMBERED_VIEW_GRACE_MS);
            debugLog('remembered-wait-start', {
              scopeKey,
              scopeMemoryKey,
              rememberedId,
              graceMs: REMEMBERED_VIEW_GRACE_MS,
            });
          } else {
            debugLog('remembered-wait-continue', {
              scopeKey,
              scopeMemoryKey,
              rememberedId,
            });
          }

          return;
        }

        if (rememberedWaitRef.current.timer) {
          clearTimeout(rememberedWaitRef.current.timer);
        }
        rememberedWaitRef.current = {
          scopeKey: '',
          timer: null,
          pending: false,
          lastRows: [],
        };

        finalizeSelection(resolved.nextRows, { allowRemembered: true, reason: 'remembered-resolved' });
      }
    );

    return () => {
      cancelled = true;
      clearRememberedWait();
      unsubscribe?.();
    };
  }, [scopeKey, scopedTypeKey, baseKey, defaultView, scopeMemoryKey]);

  // Restrict views to the active tab type; fall back to the default view when needed.
  const resolvedViews = useMemo(() => {
    const activeType = activeTypeKey || node?.type || null;
    const baseViews = ensureCatchAllView(views, defaultView);

    if (!activeType) return baseViews;

    const filtered = baseViews.filter((view) => {
      const viewType = view?.nodeType || view?.typeKey || null;
      return !viewType || viewType === activeType;
    });

    return ensureCatchAllView(filtered, defaultView);
  }, [views, defaultView, activeTypeKey, node?.type]);

  // Keep selection valid; otherwise default to catch-all.
  useEffect(() => {
    if (isLoading || isScopePending || loadedScopeKey !== scopeKey) return;
    if (!resolvedViews.length) return;

    const activeExists = resolvedViews.some((view) => resolveViewIdentity(view) === activeViewId);
    if (activeExists) {
      if (canRememberView(resolvedViews, activeViewId)) {
        setStoredViewIdForScope(scopeMemoryKey, activeViewId);
      }
      return;
    }

    const rememberedId = getStoredViewIdForScope(scopeMemoryKey);
    const rememberedExists = resolvedViews.some((view) => resolveViewIdentity(view) === rememberedId);
    const fallbackId = rememberedExists
      ? rememberedId
      : (resolveCatchAllViewId(resolvedViews) || resolveViewIdentity(resolvedViews[0]) || DEFAULT_VIEW_ID);

    setActiveViewIdState(fallbackId);
    if (canRememberView(resolvedViews, fallbackId)) {
      setStoredViewIdForScope(scopeMemoryKey, fallbackId);
    } else if (rememberedId && !rememberedExists) {
      clearStoredViewIdForScope(scopeMemoryKey);
    }

    debugLog('fallback', {
      scopeKey,
      scopeMemoryKey,
      activeViewId,
      rememberedId,
      rememberedExists,
      fallbackId,
      resolvedViewIds: resolvedViews.map((view) => resolveViewIdentity(view)).filter(Boolean),
    });
  }, [
    resolvedViews,
    activeViewId,
    scopeMemoryKey,
    isLoading,
    isScopePending,
    loadedScopeKey,
    scopeKey,
  ]);

  const setActiveViewId = (nextActiveViewIdOrUpdater) => {
    setActiveViewIdState((prev) => {
      const resolved = typeof nextActiveViewIdOrUpdater === 'function'
        ? nextActiveViewIdOrUpdater(prev)
        : nextActiveViewIdOrUpdater;
      const normalized = String(resolved || '').trim() || DEFAULT_VIEW_ID;
      if (canRememberView(resolvedViews, normalized)) {
        setStoredViewIdForScope(scopeMemoryKey, normalized);
      }

      debugLog('setActiveViewId', {
        scopeKey,
        scopeMemoryKey,
        previous: prev,
        next: normalized,
        rememberable: canRememberView(resolvedViews, normalized),
      });

      return normalized;
    });
  };

  const activeView = useMemo(() => {
    const list = resolvedViews;
    return list.find((view) => resolveViewIdentity(view) === activeViewId) || list[0] || null;
  }, [resolvedViews, activeViewId]);

  return {
    views: resolvedViews,
    activeViewId,
    setActiveViewId,
    activeView,
    isLoading: isLoading || isScopePending,
  };
}
